import React, { useCallback, useEffect, useState } from "react";
import { ArrowRight, ChevronDown, BookOpen, Sparkles, MoveRight } from "lucide-react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import Reveal from "../components/site/Reveal";
import BookCard3D from "../components/site/book/BookCard3D";
import ReservationModal from "../components/site/ui/ReservationModal";
import BookSchema from "../components/site/BookSchema";
import { BOOK_CHAPTERS } from "../data/bookChapters";
import { trackEvent } from "../lib/track";
import { amazonBookLinkProps } from "../lib/externalLinks";

/**
 * /book — Standalone editorial sales page for Authentic Momentum.
 *
 * Architecturally independent from the homepage. No `#book` scroll
 * anchor; this is the route every "the book" CTA across the site
 * lands on. Narrative flow follows a deliberate publishing-house
 * sequence: cinematic hero → origin story → what it helps you see
 * → chapter experience → first-chapter sample → who this is for →
 * how it connects into the wider system → final CTA. Stripe
 * checkout is wired directly via ReservationModal listening for
 * `am:open-book-checkout` so the same affordance fires from the
 * hero, the chapter sample, and the closing CTA.
 */

const HERO_SUBHEAD =
  "A guide for recognizing what has been quietly slowing you down, and learning to move with the signal underneath the noise.";

const ORIGIN_PARAGRAPHS = [
  "I did not set out to write a book. I set out to stop forcing.",
  "What you are about to read started as private notes. The kind you write at 2 a.m. after you finally see the pattern you have been losing energy to for years. The kind you write when the next strategy isn't the problem, and the next push isn't the answer, and there is something quieter that needs to be named first.",
  "Once those notes started to repeat across the people I was working with, I knew they were not just personal. They were the framework.",
  "Authentic Momentum the book came first. The Clarity Snapshot, the Personal Action Plan, and the Clarity Reset Session were all built afterward as living extensions of the ideas you will read here. They are how the framework keeps working long after you close the last page.",
];

const SEEING_BLOCKS = [
  {
    title: "Patterns",
    body:
      "The loops that look like effort but are actually avoidance. The decisions you keep re-making because the real decision is one layer underneath.",
  },
  {
    title: "Friction",
    body:
      "Where you are pulling against your own signal. What looks like a discipline problem is often a frequency problem.",
  },
  {
    title: "Bottlenecks",
    body:
      "The single quiet constraint that is shaping ten visible symptoms. Name it once, and the symptoms relax.",
  },
  {
    title: "Movement",
    body:
      "What real momentum feels like. Not louder. Not faster. Cleaner, more aligned, harder to confuse with hustle.",
  },
];

const CHAPTER_ONE_SAMPLE = [
  "You know that feeling when you are trying so hard to make something work that you are actually making it harder than it needs to be?",
  "The strategy is right. The plan is sound. You have done the reading and you have done the work. And yet, the more you push, the further the outcome seems to retreat.",
  "This is where Authentic Momentum begins.",
  "Not with another strategy. Not with more force. With the quiet recognition that your energy may be asking for a different way to move.",
  "Most of us were taught that effort is the answer. That if something is not working, the right response is to want it more, plan harder, sacrifice further. That formula is not entirely wrong. It is just incomplete. Because effort applied to the wrong frequency does not create momentum. It creates exhaustion.",
  "There is a different starting point. A signal underneath the noise of what we think we should be doing. When you learn to recognize it, the strategy you already have stops fighting you. The work gets cleaner. The decisions get smaller. The result moves toward you instead of away from you.",
  "This book is the field guide for finding that signal.",
];

const FOR_YOU_IF = [
  "You are doing the right work and the wrong work is still showing up in your results.",
  "You have read the strategy books, taken the courses, made the plans, and the gap between what you know and how you feel is widening, not closing.",
  "You can feel that you are misaligned somewhere but you cannot quite name where, and naming it has become the most important next step.",
  "You are tired of forcing decisions that should feel obvious by now.",
  "You want a framework you can carry into every part of your life without rebuilding from scratch each time.",
  "You suspect that the quiet voice you have been overriding has been right more often than the loud one you have been listening to.",
];

const SYSTEM_LAYERS = [
  {
    name: "Clarity Snapshot",
    price: "Free",
    purpose: "Find the signal underneath your current friction in three minutes.",
    href: "/frequency-snapshot",
    cta: "Take the Snapshot",
    testid: "book-system-snapshot",
  },
  {
    name: "Personal Action Plan",
    price: "$49.99",
    purpose: "Turn the book's framework into a personal sequence of next moves you can actually keep.",
    href: "/start-here#step-blueprint",
    cta: "See the Plan",
    testid: "book-system-plan",
  },
  {
    name: "Clarity Reset Session",
    price: "$149",
    purpose: "One focused 30 minutes with Cyrus to name the frame you cannot see from the inside.",
    href: "/orientation-reset/schedule",
    cta: "Book a Reset",
    testid: "book-system-reset",
  },
];

const fireCheckout = (surface) => {
  trackEvent("book_amazon_clicked", { surface });
};

const scrollToSample = () => {
  trackEvent("book_page_preview_clicked");
  const el = document.querySelector('[data-testid="book-chapter-sample"]');
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const BookPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedChapter, setExpandedChapter] = useState(null);

  const openCheckout = useCallback(() => setModalOpen(true), []);

  // Same global event used on the homepage so any sub-component can
  // open the Stripe Reservation modal without prop drilling.
  useEffect(() => {
    const handler = () => setModalOpen(true);
    window.addEventListener("am:open-book-checkout", handler);
    return () => window.removeEventListener("am:open-book-checkout", handler);
  }, []);

  useEffect(() => {
    document.title = "Authentic Momentum · The Book | Cyrus Prescod";
    trackEvent("book_page_viewed");
  }, []);

  return (
    <>
      <BookSchema />
      <Navigation />
      <main
        data-testid="book-page"
        className="relative min-h-screen overflow-hidden book-page-shell"
      >
        {/* ─── CINEMATIC HERO ─────────────────────────────────────── */}
        <section
          data-testid="book-hero"
          className="relative pt-32 md:pt-40 pb-24 md:pb-32 book-hero"
        >
          <div className="book-hero-aura" aria-hidden />
          <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-center">
            <Reveal className="lg:col-span-5 order-2 lg:order-1">
              <div className="book-hero-mockup">
                <BookCard3D />
              </div>
            </Reveal>

            <div className="lg:col-span-7 order-1 lg:order-2">
              <Reveal>
                <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-7">
                  The Book · A Field Guide
                </p>
                <h1
                  data-testid="book-hero-title"
                  className="font-serif text-5xl sm:text-6xl lg:text-7xl text-themed leading-[0.98] tracking-tight font-light"
                >
                  Authentic{" "}
                  <span className="italic gold-gradient-text">Momentum.</span>
                </h1>
                <p
                  data-testid="book-hero-subhead"
                  className="mt-8 max-w-xl text-themed-muted text-base md:text-lg leading-relaxed"
                >
                  {HERO_SUBHEAD}
                </p>
              </Reveal>

              <Reveal delay={140}>
                <div className="mt-10 flex flex-wrap items-baseline gap-x-4 gap-y-2">
                  <div>
                    <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85">
                      Investment
                    </p>
                    <p className="font-serif text-4xl text-gold mt-1">$19.95</p>
                  </div>
                  <p className="text-themed-muted/80 text-sm pb-1">
                    Instant digital delivery. PDF format. Yours to keep.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={220}>
                <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <a
                    {...amazonBookLinkProps}
                    data-testid="book-hero-buy-cta"
                    onClick={() => fireCheckout("hero")}
                    className="btn-gold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                  >
                    Buy on Amazon · $19.95 <ArrowRight size={14} />
                  </a>
                  <button
                    type="button"
                    data-testid="book-hero-preview-cta"
                    onClick={scrollToSample}
                    className="btn-ghost-gold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                  >
                    Read a Preview <ChevronDown size={14} />
                  </button>
                </div>
              </Reveal>

              <Reveal delay={320}>
                <p className="mt-7 text-[11px] tracking-[0.22em] uppercase text-themed-muted/70">
                  Self-published · Independent · Written by hand
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ─── ORIGIN STORY ───────────────────────────────────────── */}
        <section
          data-testid="book-origin"
          className="relative py-24 md:py-32 border-t border-gold/10"
        >
          <div className="max-w-3xl mx-auto px-6 md:px-10">
            <Reveal>
              <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
                How this began
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight font-light tracking-tight">
                The book that{" "}
                <span className="italic gold-gradient-text">
                  started everything.
                </span>
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <div
                data-testid="book-origin-body"
                className="mt-10 max-w-3xl space-y-6 text-white/85 leading-relaxed text-[16px] md:text-[17.5px]"
              >
                {ORIGIN_PARAGRAPHS.map((p, i) => (
                  <p key={i} className={i === 0 ? "text-themed font-serif italic text-2xl md:text-3xl leading-[1.25] text-gold/90" : ""}>
                    {p}
                  </p>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── WHAT THIS BOOK HELPS PEOPLE SEE ───────────────────── */}
        <section
          data-testid="book-seeing"
          className="relative py-24 md:py-32 border-t border-gold/10"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <Reveal>
              <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
                What it helps you see
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight font-light tracking-tight max-w-3xl">
                Four things become{" "}
                <span className="italic gold-gradient-text">visible</span>{" "}
                when you read it.
              </h2>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              {SEEING_BLOCKS.map((s, i) => (
                <Reveal key={s.title} delay={120 + i * 70}>
                  <div
                    data-testid={`book-seeing-card-${i}`}
                    className="book-seeing-card glass-card p-7 md:p-8 h-full"
                  >
                    <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-4">
                      0{i + 1}
                    </p>
                    <h3 className="font-serif text-2xl text-themed font-light mb-3">
                      {s.title}
                    </h3>
                    <p className="text-themed-muted leading-relaxed text-[15px]">
                      {s.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── CHAPTER EXPERIENCE ─────────────────────────────────── */}
        <section
          data-testid="book-chapters"
          className="relative py-24 md:py-32 border-t border-gold/10"
        >
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <Reveal>
              <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
                Nine chapters
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight font-light tracking-tight text-center max-w-3xl mx-auto">
                A clean sequence of{" "}
                <span className="italic gold-gradient-text">
                  recognition.
                </span>
              </h2>
              <p className="mt-7 max-w-2xl mx-auto text-center text-themed-muted leading-relaxed">
                Each chapter is short enough to read in one sitting. Together they form a complete framework you can return to as your situation changes.
              </p>
            </Reveal>

            <div className="mt-14 space-y-3">
              {BOOK_CHAPTERS.map((c, i) => {
                const isOpen = expandedChapter === i;
                return (
                  <Reveal key={c.title} delay={80 + i * 40}>
                    <button
                      type="button"
                      data-testid={`book-chapter-${i}`}
                      onClick={() => {
                        setExpandedChapter(isOpen ? null : i);
                        trackEvent("book_chapter_expanded", { chapter: i + 1, title: c.title });
                      }}
                      aria-expanded={isOpen}
                      className={`book-chapter-row w-full text-left ${isOpen ? "is-open" : ""}`}
                    >
                      <div className="book-chapter-head">
                        <span className="book-chapter-num">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="book-chapter-title">{c.title}</span>
                        <ChevronDown
                          size={16}
                          className={`book-chapter-chev ${isOpen ? "is-open" : ""}`}
                          aria-hidden
                        />
                      </div>
                      <div className={`book-chapter-brief ${isOpen ? "is-open" : ""}`}>
                        <p className="text-themed-muted leading-relaxed text-[15px] pt-3 pb-1">
                          {c.brief}
                        </p>
                      </div>
                    </button>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── CHAPTER 1 SAMPLE ───────────────────────────────────── */}
        <section
          data-testid="book-chapter-sample"
          className="relative py-28 md:py-36 border-t border-gold/10 book-sample-section"
        >
          <div className="book-sample-aura" aria-hidden />
          <div className="relative max-w-3xl mx-auto px-6 md:px-10">
            <Reveal>
              <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
                Chapter 1 · Opening Frequency
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight font-light tracking-tight">
                Read{" "}
                <span className="italic gold-gradient-text">the opening.</span>
              </h2>
            </Reveal>

            <Reveal delay={120}>
              <article
                data-testid="book-chapter-sample-body"
                className="mt-12 book-sample-prose"
              >
                {CHAPTER_ONE_SAMPLE.map((p, i) => (
                  <p
                    key={i}
                    className={i === 0 ? "book-sample-lead" : i === 2 ? "book-sample-break" : ""}
                  >
                    {p}
                  </p>
                ))}
                <div className="book-sample-fade" aria-hidden />
              </article>
            </Reveal>

            <Reveal delay={220}>
              <div className="mt-14 flex flex-col sm:flex-row gap-4 items-center">
                <a
                  {...amazonBookLinkProps}
                  data-testid="book-sample-buy-cta"
                  onClick={() => fireCheckout("sample")}
                  className="btn-gold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  Continue on Amazon · $19.95 <ArrowRight size={14} />
                </a>
                <p className="text-themed-muted/85 text-[13px] leading-relaxed">
                  Eight more chapters wait on the other side of this page.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* ─── WHO THIS IS FOR ────────────────────────────────────── */}
        <section
          data-testid="book-for-you"
          className="relative py-24 md:py-32 border-t border-gold/10"
        >
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <Reveal>
              <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
                This book is for you if...
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight font-light tracking-tight max-w-3xl">
                You recognize{" "}
                <span className="italic gold-gradient-text">
                  yourself
                </span>{" "}
                in any of this.
              </h2>
            </Reveal>

            <ul
              data-testid="book-for-you-list"
              className="mt-14 space-y-5 md:space-y-6"
            >
              {FOR_YOU_IF.map((line, i) => (
                <Reveal key={i} delay={80 + i * 40}>
                  <li
                    data-testid={`book-for-you-item-${i}`}
                    className="book-for-you-row flex gap-5 md:gap-7 items-start max-w-3xl"
                  >
                    <span className="book-for-you-mark" aria-hidden />
                    <p className="text-white/85 text-[16px] md:text-[17.5px] leading-relaxed">
                      {line}
                    </p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </section>

        {/* ─── SYSTEM CONNECTION ──────────────────────────────────── */}
        <section
          data-testid="book-system"
          className="relative py-24 md:py-32 border-t border-gold/10"
        >
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <Reveal>
              <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
                Built from the book
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-themed leading-tight font-light tracking-tight text-center max-w-3xl mx-auto">
                The framework{" "}
                <span className="italic gold-gradient-text">
                  keeps going.
                </span>
              </h2>
              <p className="mt-7 max-w-2xl mx-auto text-center text-themed-muted leading-relaxed">
                Each of these was built afterward as a way to keep working with the framework once the last chapter ends. Optional. Sequenced. Always rooted in the same posture.
              </p>
            </Reveal>

            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {SYSTEM_LAYERS.map((s, i) => (
                <Reveal key={s.name} delay={120 + i * 100}>
                  <a
                    href={s.href}
                    data-testid={s.testid}
                    onClick={() => trackEvent("book_system_card_clicked", { layer: s.name })}
                    className="book-system-card glass-card p-7 md:p-8 h-full flex flex-col"
                  >
                    <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
                      Layer 0{i + 1}
                    </p>
                    <h3 className="font-serif text-2xl text-themed font-light mb-2">
                      {s.name}
                    </h3>
                    <p className="text-gold/85 text-sm mb-4">{s.price}</p>
                    <p className="text-themed-muted leading-relaxed text-[15px] flex-grow">
                      {s.purpose}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-gold group-hover:text-[#F3E5AB] book-system-cta">
                      {s.cta} <MoveRight size={12} />
                    </span>
                  </a>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FINAL CTA ──────────────────────────────────────────── */}
        <section
          data-testid="book-final-cta"
          className="relative py-28 md:py-40 border-t border-gold/10 book-final-cta"
        >
          <div className="book-final-aura" aria-hidden />
          <div className="relative max-w-3xl mx-auto px-6 md:px-10 text-center">
            <Reveal>
              <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
                Begin
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl text-themed leading-tight font-light tracking-tight">
                Read the book that{" "}
                <span className="italic gold-gradient-text">
                  came first.
                </span>
              </h2>
              <p className="mt-7 text-themed-muted leading-relaxed max-w-xl mx-auto">
                One investment. One framework. Yours for life. Read it slowly. Let the right lines find you.
              </p>
            </Reveal>

            <Reveal delay={140}>
              <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  {...amazonBookLinkProps}
                  data-testid="book-final-buy-cta"
                  onClick={() => fireCheckout("final")}
                  className="btn-gold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  Buy on Amazon · $19.95 <ArrowRight size={14} />
                </a>
                <a
                  href="/frequency-snapshot"
                  data-testid="book-final-snapshot-cta"
                  onClick={() => trackEvent("book_final_snapshot_clicked")}
                  className="btn-ghost-gold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  Start with the free Clarity Snapshot
                </a>
              </div>
            </Reveal>

            <Reveal delay={260}>
              <div className="mt-16 flex items-center justify-center gap-3 text-themed-muted/75 text-[11px] tracking-[0.22em] uppercase">
                <Sparkles size={12} className="text-gold/80" aria-hidden />
                Written, edited, and published by Cyrus Prescod
                <Sparkles size={12} className="text-gold/80" aria-hidden />
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />

      <ReservationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        product="book"
        testIdPrefix="book"
        eyebrow="Continue to Checkout"
        title="Step into the first chapter."
        description="Secure checkout via Stripe. You will be redirected to complete your purchase. Instant PDF delivery to your email after payment."
        submitLabel="Buy Authentic Momentum · $19.99"
        busyLabel="Preparing checkout…"
      />
    </>
  );
};

export default BookPage;
